import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './Card';
import { Progress } from './Progress';
import { cn } from '../../utils/cn';
import { HelpCircle } from 'lucide-react';

const QuestionCard = ({ 
  question, 
  currentQuestion, 
  totalQuestions, 
  selectedAnswer, 
  onAnswerSelect,
  className 
}) => {
  const options = [
    { value: 1, label: 'Strongly Disagree', color: 'bg-gradient-to-r from-red-500 to-pink-500', ring: 'ring-red-200' },
    { value: 2, label: 'Disagree', color: 'bg-gradient-to-r from-orange-500 to-red-400', ring: 'ring-orange-200' },
    { value: 3, label: 'Neutral', color: 'bg-gradient-to-r from-amber-500 to-yellow-500', ring: 'ring-amber-200' },
    { value: 4, label: 'Agree', color: 'bg-gradient-to-r from-blue-500 to-indigo-500', ring: 'ring-blue-200' },
    { value: 5, label: 'Strongly Agree', color: 'bg-gradient-to-r from-emerald-500 to-teal-500', ring: 'ring-emerald-200' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
      className={cn("w-full max-w-4xl mx-auto", className)}
    >
      <Card className="shadow-2xl border-2 border-indigo-100/60 bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 backdrop-blur-sm">
        <CardContent className="p-6">
          {/* Progress Section */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-gray-600">
                Question {currentQuestion} of {totalQuestions}
              </span>
              <span className="text-sm font-medium text-blue-600">
                {Math.round((currentQuestion / totalQuestions) * 100)}% Complete
              </span>
            </div>
            <Progress value={currentQuestion} max={totalQuestions} className="h-2" />
          </div>

          {/* Question */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6"
          >
            <div className="flex items-start justify-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900 leading-relaxed text-center">
                "{question.text}"
              </h2>
              {question.help && (
                <div className="group relative">
                  <HelpCircle className="w-5 h-5 text-gray-400 hover:text-blue-500 cursor-help" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                    {question.help}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Answer Options */}
          <div className="space-y-2">
            {options.map((option, index) => (
              <motion.button
                key={option.value}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                onClick={() => onAnswerSelect(option.value)}
                onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
                onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
                className={cn(
                  "w-full p-3 rounded-xl border-2 transition-all duration-300 text-left group hover:shadow-xl hover:scale-[1.01] touch-manipulation",
                  selectedAnswer === option.value
                    ? "border-indigo-400 bg-gradient-to-r from-indigo-50 to-purple-50 shadow-xl scale-[1.02] ring-4 ring-indigo-100"
                    : "border-slate-200 bg-gradient-to-r from-white to-slate-50/50 hover:border-indigo-300 hover:from-indigo-50/50 hover:to-purple-50/50"
                )}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-center space-x-4">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold transition-all duration-300 shadow-lg",
                    selectedAnswer === option.value ? `${option.color} shadow-xl ring-2 ring-white` : "bg-gradient-to-r from-slate-300 to-slate-400 group-hover:from-indigo-400 group-hover:to-purple-400"
                  )}>
                    {option.value}
                  </div>
                  <span className={cn(
                    "font-medium transition-colors duration-200",
                    selectedAnswer === option.value ? "text-blue-700" : "text-gray-700 group-hover:text-gray-900"
                  )}>
                    {option.label}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>


        </CardContent>
      </Card>
    </motion.div>
  );
};

export { QuestionCard };